import _ from 'underscore';

import {assert} from 'Asserts';
import ArgType from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {Range} from 'notebook/tables/excess/FunctionConstants';
import {TypedValueSpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import * as util from 'notebook/tables/excess/Util';
import {lookupTypedValue} from 'notebook/tables/excess/util/lookupTypedValue';
import {coerceToPrimitive} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

export default {
  name: 'LOOKUP',
  category: FunctionCategory.LOOKUP,
  shortDescription: 'Look up a value.',
  description:
    'Looks through a row or column for a key and returns the value of the cell in a result range located in the same position as the search row or column. Returns the closest match. Behavior can be misleading—XLOOKUP is recommended instead.',
  params: [
    {
      name: 'search_key',
      type: ArgType.singularValue,
      description: 'The value to search for. For example, 5, "LOT123", or A2.',
    },
    {
      name: 'search_range',
      type: ArgType.cellRange,
      // since we are searching over these values, not coercing them, we can have errors in the range.
      allowError: true,
      description: 'The range to consider for the search. This range must be a singular row or column.',
    },
    {
      name: 'result_range',
      type: ArgType.cellRange,
      optional: true,
      description:
        "The range to consider for the result. This range's row or column size should be the same as search_range.",
      // since we are searching over these values, not coercing them, we can have errors in the range.
      allowError: true,
    },
  ],

  evalWithTypedValues: (
    searchKey: TypedValue,
    searchRange: Range<TypedValue>,
    resultRange: Range<TypedValue>
  ): TypedValue => {
    const {result, searchList, resultList} = lookupTypedValue(searchKey, searchRange, resultRange, 'LOOKUP');
    if (!result) {
      const searchKeyPrimitive = coerceToPrimitive(searchKey);
      const rawSearchList = searchList.map((typedValue: TypedValue) => coerceToPrimitive(typedValue));
      // We make the assumption that searchList is sorted (Excel and Google Sheets assume this)
      // sortedIndex compares using < operator, which matches Excel/Sheets for sorting strings vs text
      // TODO(BNCH-2969): Return closest value, but surface a warning.
      const index = _.sortedIndex(rawSearchList, searchKeyPrimitive) - 1;
      assert(searchList[index + 1] !== searchKey, 'searchKey found despite indexOf(searchKey) < 0');
      if (index < 0 || typeof searchKeyPrimitive !== typeof rawSearchList[index]) {
        throw new util.NotApplicableError(`Did not find value "${searchKeyPrimitive}" in LOOKUP evaluation.`);
      }
      return resultList[index];
    } else {
      return result;
    }
  },
} satisfies TypedValueSpreadsheetFunction;
